// -----------------------------------------------------------------------------
// PortfolioOverview.jsx
// Level 1 — Portfolio Command Center
//
// PURPOSE:
// - Primary landing page for returning homeowners
// - Aggregated view across ALL properties owned
// - Valuation-first hierarchy
// - Portfolio-level actions only (e.g. Add Property)
// - Voice-aware, but not voice-dependent
//
// LEVEL MODEL:
// - Level 1: Portfolio (this page)
// - Level 2: Property (single address deep dive)
// -----------------------------------------------------------------------------

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useVoiceAssistant from "../voice/useVoiceAssistant.js";
import PortfolioMap from "./PortfolioMap.jsx";
import NotificationBell from "../Notifications/NotificationBell.jsx";
import InviteUserModal from "../InviteUserModal.jsx";
import { usePermissions } from "../../hooks/usePermissions.js";
import { useAuth } from "../../context/AuthContext.jsx";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";

/**
 * slugify - must match PropertyDashboard.js and PropertyDetails.js
 */
const slugify = (str = "") =>
  str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");

export default function PortfolioOverview() {
  const navigate = useNavigate();
  const { lastResponse } = useVoiceAssistant();
  const { canInviteAtPortfolioLevel } = usePermissions();
  const { getToken, logout } = useAuth();
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Refs for voice-driven focus
  const valuationRef = useRef(null);
  const mortgageRef = useRef(null);
  const documentsRef = useRef(null);
  const maintenanceRef = useRef(null);
  const propertiesRef = useRef(null);

  const [highlight, setHighlight] = useState(null);
  const [guidedSection, setGuidedSection] = useState(null);
  const [properties, setProperties] = useState([]);

  // ---------------------------------------------------------------------------
  // FETCH PROPERTIES
  // ---------------------------------------------------------------------------
  useEffect(() => {
    async function fetchProperties() {
      try {
        const token = getToken();
        const res = await fetch("/api/properties", {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (res.ok) {
          const data = await res.json();
          setProperties(data);
        }
      } catch (err) {
        console.error("Failed to fetch properties:", err);
      }
    }
    fetchProperties();
  }, [getToken]);

  // ---------------------------------------------------------------------------
  // VOICE → UI FOCUS HANDLING (PORTFOLIO CONTEXT)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!lastResponse || !lastResponse.actions) return;

    const action = lastResponse.actions[0];
    if (!action || action.type !== "FOCUS_SECTION") return;

    const sectionMap = {
      valuation: valuationRef,
      mortgage: mortgageRef,
      documents: documentsRef,
      maintenance: maintenanceRef,
      properties: propertiesRef,
    };

    const targetRef = sectionMap[action.section];

    if (targetRef?.current) {
      targetRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      setHighlight(action.section);
      setGuidedSection(action.section);

      setTimeout(() => {
        setHighlight(null);
        setGuidedSection(null);
      }, 2200);
    }
  }, [lastResponse]);

  const highlightClass = (key) =>
    highlight === key
      ? "ring-2 ring-offset-2 transition"
      : "";

  const guidanceText = {
    valuation: "Based on your question, here’s your portfolio valuation.",
    mortgage: "Based on your question, here’s your mortgage overview.",
    documents: "Based on your question, here are your documents.",
    maintenance: "Based on your question, here’s your maintenance snapshot.",
    properties: "Based on your question, here are your properties.",
  };

  // ---------------------------------------------------------------------------
  // LEVEL-1 PORTFOLIO CALCULATIONS (DYNAMIC)
  // ---------------------------------------------------------------------------
  const calculatePortfolio = () => {
    let totalValue = 0;
    let totalMortgage = 0;
    let earliestRenewal = null;

    properties.forEach((property) => {
      // Add property value
      const value = property.currentValue || property.valuation?.estimate || 0;
      totalValue += value;

      // Add mortgage balance
      const mortgageBalance = property.mortgage?.balance || 0;
      totalMortgage += mortgageBalance;

      // Track earliest renewal date
      if (property.mortgage?.renewalDate) {
        const renewalDate = new Date(property.mortgage.renewalDate);
        if (!earliestRenewal || renewalDate < earliestRenewal) {
          earliestRenewal = renewalDate;
        }
      }
    });

    const equity = totalValue - totalMortgage;
    const renewalYear = earliestRenewal ? earliestRenewal.getFullYear() : null;

    return {
      totalValue,
      totalMortgage,
      equity,
      renewalYear,
      propertyCount: properties.length,
    };
  };

  const portfolio = calculatePortfolio();

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  return (
    <div className="px-6 py-10 space-y-10">

      {/* ---------------------------------------------------------
         HEADER (PORTFOLIO LEVEL)
      --------------------------------------------------------- */}
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold mb-1">
            Portfolio Overview
          </h1>
          <p className="text-sm text-gray-600">
            Your home command center — across all properties you own.
          </p>
        </div>

        {/* Header Actions */}
        <div className="flex items-center gap-4">
          {/* Notifications */}
          <NotificationBell userId="owner" />

          {/* Invite User = PORTFOLIO ACTION (owners/co-owners only) */}
          {canInviteAtPortfolioLevel && (
            <button
              onClick={() => setShowInviteModal(true)}
              className="px-4 py-2 rounded-full text-sm font-medium shadow-sm transition border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            >
              Invite User
            </button>
          )}

          {/* Add Property = PORTFOLIO ACTION */}
          <button
            onClick={() => navigate("/add-property")}
            className="px-4 py-2 rounded-full text-sm font-medium shadow-sm transition"
            style={{
              backgroundColor: "#CC0202",
              color: "#ffffff",
            }}
          >
            + Add Property
          </button>

          {/* Log Out */}
          <button
            onClick={() => logout().then(() => navigate('/onboarding'))}
            className="px-4 py-2 rounded-full text-sm font-medium shadow-sm transition border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700"
            title="Log out"
          >
            Log out
          </button>
        </div>
      </header>

      {/* Invite User Modal */}
      <InviteUserModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        properties={properties}
      />

      {/* ---------------------------------------------------------
         HERO SUMMARY (VALUATION-FIRST) + ACTION ITEMS SIDE BY SIDE
      --------------------------------------------------------- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Total Portfolio Value Card */}
        <section
          ref={valuationRef}
          className={`p-6 bg-white rounded-xl shadow ${highlightClass("valuation")}`}
          style={{
            borderColor: highlight === "valuation" ? "#CC0202" : "transparent",
          }}
        >
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-500">Total Portfolio Value</p>
              <p className="text-4xl font-bold">
                {portfolio.totalValue > 0
                  ? `$${portfolio.totalValue.toLocaleString()}`
                  : "$0"}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Across {portfolio.propertyCount} {portfolio.propertyCount === 1 ? "property" : "properties"}
              </p>
            </div>

            <div className="flex gap-8 text-sm">
              <div>
                <p className="text-gray-500">Total Mortgage Balance</p>
                <p className="font-semibold">
                  {portfolio.totalMortgage > 0
                    ? `$${portfolio.totalMortgage.toLocaleString()}`
                    : "No mortgages"}
                </p>
              </div>

              <div>
                <p className="text-gray-500">Total Equity</p>
                <p className="font-semibold">
                  {portfolio.equity > 0
                    ? `$${portfolio.equity.toLocaleString()}`
                    : "$0"}
                </p>
                {portfolio.totalValue > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {((portfolio.equity / portfolio.totalValue) * 100).toFixed(1)}% ownership
                  </p>
                )}
              </div>
            </div>

            <div className="text-sm text-gray-600">
              {portfolio.propertyCount > 0 ? (
                <>
                  Portfolio appears stable.
                  {portfolio.renewalYear && (
                    <> Next mortgage renewal expected in {portfolio.renewalYear}.</>
                  )}
                </>
              ) : (
                "Add your first property to see portfolio metrics."
              )}
            </div>

            <div className="text-sm text-gray-500 italic">
              Try asking: "What's my equity?" or "When is my renewal?"
            </div>
          </div>
        </section>

        {/* Action Items Card */}
        <section className="p-6 bg-white rounded-xl shadow border-l-4 border-hn-red">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-hn-dark">Action Items</h2>
            <span className="text-xs text-gray-500">Across all properties</span>
          </div>

          <div className="space-y-3">
            {/* Check for upcoming mortgage renewals */}
            {properties.some(p => {
              if (!p.mortgage?.renewalDate) return false;
              const renewalDate = new Date(p.mortgage.renewalDate);
              const monthsUntilRenewal = (renewalDate - new Date()) / (1000 * 60 * 60 * 24 * 30);
              return monthsUntilRenewal <= 12 && monthsUntilRenewal > 0;
            }) && (
              <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <span className="text-xl">⏰</span>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-amber-900">Mortgage Renewal Approaching</h3>
                  <p className="text-xs text-amber-700 mt-1">
                    {properties
                      .filter(p => {
                        if (!p.mortgage?.renewalDate) return false;
                        const renewalDate = new Date(p.mortgage.renewalDate);
                        const monthsUntilRenewal = (renewalDate - new Date()) / (1000 * 60 * 60 * 24 * 30);
                        return monthsUntilRenewal <= 12 && monthsUntilRenewal > 0;
                      })
                      .map(p => {
                        const renewalDate = new Date(p.mortgage.renewalDate);
                        const monthsUntilRenewal = Math.round((renewalDate - new Date()) / (1000 * 60 * 60 * 24 * 30));
                        return `${p.name || p.address} - ${monthsUntilRenewal} months`;
                      })
                      .join(', ')}
                  </p>
                  <button className="text-xs text-amber-800 underline mt-1">Review mortgage options</button>
                </div>
              </div>
            )}

            {/* Seasonal maintenance reminder */}
            {(() => {
              const month = new Date().getMonth();
              const season = month >= 2 && month <= 4 ? 'Spring' :
                            month >= 5 && month <= 7 ? 'Summer' :
                            month >= 8 && month <= 10 ? 'Fall' : 'Winter';
              const tasks = {
                Spring: ['Clean gutters', 'Inspect roof', 'Service AC'],
                Summer: ['Check sprinklers', 'Trim trees', 'Seal driveway'],
                Fall: ['Winterize outdoor faucets', 'Clean furnace', 'Check insulation'],
                Winter: ['Clear snow/ice', 'Check heating system', 'Prevent frozen pipes']
              };
              return (
                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <span className="text-xl">🔧</span>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-blue-900">{season} Maintenance Checklist</h3>
                    <p className="text-xs text-blue-700 mt-1">
                      Recommended tasks: {tasks[season].slice(0, 3).join(', ')}
                    </p>
                    <button className="text-xs text-blue-800 underline mt-1">View full checklist</button>
                  </div>
                </div>
              );
            })()}

            {/* Property insurance check */}
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
              <span className="text-xl">🛡️</span>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-green-900">Insurance Review</h3>
                <p className="text-xs text-green-700 mt-1">
                  Annual insurance review recommended - Compare rates to ensure best coverage
                </p>
                <button className="text-xs text-green-800 underline mt-1">Review policies</button>
              </div>
            </div>

            {/* No urgent items */}
            {!properties.some(p => {
              if (!p.mortgage?.renewalDate) return false;
              const renewalDate = new Date(p.mortgage.renewalDate);
              const monthsUntilRenewal = (renewalDate - new Date()) / (1000 * 60 * 60 * 24 * 30);
              return monthsUntilRenewal <= 12 && monthsUntilRenewal > 0;
            }) && properties.length > 0 && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <span className="text-xl">✅</span>
                <p className="text-sm text-gray-600">
                  No urgent action items. Your portfolio is in good standing.
                </p>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* ---------------------------------------------------------
         INTERACTIVE VISUALIZATIONS
      --------------------------------------------------------- */}
      {portfolio.propertyCount > 0 && (() => {
        // Brand colors
        const BRAND_RED = "#CC0202";
        const BRAND_DARK = "#262626";
        const BRAND_GRAY = "#6B7280";
        const BRAND_LIGHT_GRAY = "#D1D5DB";

        // Property colors palette (brand-inspired)
        const PROPERTY_COLORS = [BRAND_RED, "#E63946", "#FF6B6B", "#FF8FA3", "#FFB3C1"];

        // Equity vs Mortgage data for pie chart
        const equityMortgageData = [
          { name: "Equity", value: portfolio.equity, color: BRAND_RED },
          { name: "Mortgage", value: portfolio.totalMortgage, color: BRAND_LIGHT_GRAY },
        ];

        // Portfolio composition data for pie chart
        const compositionData = properties.map((property, index) => ({
          name: property.name || property.address?.split(",")[0] || `Property ${index + 1}`,
          value: property.currentValue || property.valuation?.estimate || 0,
          color: PROPERTY_COLORS[index % PROPERTY_COLORS.length],
        }));

        // Property values for bar chart
        const barData = properties.map((property, index) => ({
          name: property.name || property.address?.split(",")[0] || `Property ${index + 1}`,
          value: property.currentValue || property.valuation?.estimate || 0,
          equity: (property.currentValue || property.valuation?.estimate || 0) - (property.mortgage?.balance || 0),
          mortgage: property.mortgage?.balance || 0,
        }));

        // Simulated equity growth data (last 12 months)
        const generateGrowthData = () => {
          const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
          const currentMonth = new Date().getMonth();
          const data = [];
          let baseEquity = portfolio.equity * 0.85; // Start at 85% of current

          for (let i = 0; i < 12; i++) {
            const monthIndex = (currentMonth - 11 + i + 12) % 12;
            const growth = 1 + (Math.random() * 0.03 + 0.005); // 0.5-3.5% monthly growth
            baseEquity = i === 11 ? portfolio.equity : baseEquity * growth;
            data.push({
              month: months[monthIndex],
              equity: Math.round(baseEquity),
              value: Math.round(baseEquity + portfolio.totalMortgage * (1 - i * 0.005)),
            });
          }
          return data;
        };

        const growthData = generateGrowthData();

        // Custom tooltip
        const CustomTooltip = ({ active, payload, label }) => {
          if (active && payload && payload.length) {
            return (
              <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-100">
                <p className="text-sm font-medium text-gray-900">{label}</p>
                {payload.map((entry, index) => (
                  <p key={index} className="text-sm" style={{ color: entry.color }}>
                    {entry.name}: ${entry.value?.toLocaleString()}
                  </p>
                ))}
              </div>
            );
          }
          return null;
        };

        // Custom pie label
        const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
          const RADIAN = Math.PI / 180;
          const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
          const x = cx + radius * Math.cos(-midAngle * RADIAN);
          const y = cy + radius * Math.sin(-midAngle * RADIAN);

          return percent > 0.05 ? (
            <text
              x={x}
              y={y}
              fill="white"
              textAnchor="middle"
              dominantBaseline="central"
              className="text-xs font-semibold"
            >
              {`${(percent * 100).toFixed(0)}%`}
            </text>
          ) : null;
        };

        return (
          <section className="space-y-6">
            {/* TOP ROW: Portfolio Growth + Equity vs Mortgage */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Portfolio Growth */}
              <div className="bg-white rounded-xl shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold" style={{ color: BRAND_DARK }}>
                    Portfolio Growth
                  </h3>
                  <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: `${BRAND_RED}15`, color: BRAND_RED }}>
                    Last 12 Months
                  </span>
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={growthData}>
                    <defs>
                      <linearGradient id="valueGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={BRAND_RED} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={BRAND_RED} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="equityGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={BRAND_LIGHT_GRAY} stopOpacity={0.4} />
                        <stop offset="95%" stopColor={BRAND_LIGHT_GRAY} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="month"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: BRAND_GRAY, fontSize: 11 }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: BRAND_GRAY, fontSize: 11 }}
                      tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke={BRAND_RED}
                      strokeWidth={2}
                      fill="url(#valueGradient)"
                      name="Total Value"
                    />
                    <Area
                      type="monotone"
                      dataKey="equity"
                      stroke={BRAND_LIGHT_GRAY}
                      strokeWidth={2}
                      fill="url(#equityGradient)"
                      name="Equity"
                    />
                  </AreaChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-6 mt-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: BRAND_RED }} />
                    <span className="text-xs text-gray-600">Total Value</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: BRAND_LIGHT_GRAY }} />
                    <span className="text-xs text-gray-600">Equity</span>
                  </div>
                </div>
              </div>

              {/* Equity vs Mortgage Donut */}
              <div className="bg-white rounded-xl shadow p-6">
                <h3 className="text-lg font-semibold mb-2" style={{ color: BRAND_DARK }}>
                  Equity vs Mortgage
                </h3>
                <p className="text-xs text-gray-500 mb-2">Your ownership breakdown</p>
                <div className="flex items-center justify-center">
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie
                        data={equityMortgageData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={75}
                        paddingAngle={2}
                        dataKey="value"
                        labelLine={false}
                        label={renderCustomLabel}
                      >
                        {equityMortgageData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => `$${value.toLocaleString()}`}
                        contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-6 mt-2">
                  <div className="text-center">
                    <div className="flex items-center gap-2 justify-center">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: BRAND_RED }} />
                      <span className="text-sm font-medium" style={{ color: BRAND_DARK }}>
                        ${portfolio.equity.toLocaleString()}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">Equity</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center gap-2 justify-center">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: BRAND_LIGHT_GRAY }} />
                      <span className="text-sm font-medium" style={{ color: BRAND_DARK }}>
                        ${portfolio.totalMortgage.toLocaleString()}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">Mortgage</p>
                  </div>
                </div>
              </div>
            </div>

            {/* BOTTOM ROW: Two Bar Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Portfolio Mix Bar Chart */}
              <div className="bg-white rounded-xl shadow p-6">
                <h3 className="text-lg font-semibold mb-2" style={{ color: BRAND_DARK }}>
                  Portfolio Mix
                </h3>
                <p className="text-xs text-gray-500 mb-4">Value distribution by property</p>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={compositionData} layout="vertical">
                    <XAxis
                      type="number"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: BRAND_GRAY, fontSize: 11 }}
                      tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: BRAND_DARK, fontSize: 11 }}
                      width={80}
                    />
                    <Tooltip
                      formatter={(value) => `$${value.toLocaleString()}`}
                      contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                    />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                      {compositionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Property Breakdown Bar Chart */}
              <div className="bg-white rounded-xl shadow p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold" style={{ color: BRAND_DARK }}>
                    Property Breakdown
                  </h3>
                </div>
                <p className="text-xs text-gray-500 mb-4">Equity and mortgage by property</p>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={barData} layout="vertical" barGap={0}>
                    <XAxis
                      type="number"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: BRAND_GRAY, fontSize: 11 }}
                      tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: BRAND_DARK, fontSize: 11 }}
                      width={80}
                    />
                    <Tooltip
                      formatter={(value) => `$${value.toLocaleString()}`}
                      contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                    />
                    <Bar
                      dataKey="equity"
                      name="Equity"
                      fill={BRAND_RED}
                      radius={[0, 0, 0, 0]}
                      stackId="a"
                    />
                    <Bar
                      dataKey="mortgage"
                      name="Mortgage"
                      fill={BRAND_LIGHT_GRAY}
                      radius={[0, 4, 4, 0]}
                      stackId="a"
                    />
                  </BarChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-6 mt-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: BRAND_RED }} />
                    <span className="text-xs text-gray-600">Equity</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: BRAND_LIGHT_GRAY }} />
                    <span className="text-xs text-gray-600">Mortgage</span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* ---------------------------------------------------------
         PROPERTIES (PORTFOLIO LIST) - MOVED UP
      --------------------------------------------------------- */}
      <section ref={propertiesRef}>
        <h2 className="text-lg font-medium mb-4">Your Properties</h2>

        <div className="space-y-4">
          {properties.length > 0 ? (
            properties.map((property) => {
              const valuation = property.currentValue || property.valuation?.estimate;
              const displayName = property.name && property.name !== property.address
                ? property.name
                : null;

              return (
                <div
                  key={property.id}
                  className="p-5 bg-white rounded-xl shadow cursor-pointer hover:shadow-md transition"
                  onClick={() => navigate(`/property/${property.address ? slugify(property.address) : `property-${property.id}`}`)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      {displayName && (
                        <h3 className="font-medium text-hn-dark">{displayName}</h3>
                      )}
                      <p className={`${displayName ? 'text-sm' : 'font-medium'} text-gray-600`}>
                        {property.address || "No address"}
                      </p>
                      {property.type && (
                        <p className="text-xs text-gray-500 mt-1">
                          {property.type}
                          {property.sqft && ` • ${property.sqft.toLocaleString()} sq ft`}
                        </p>
                      )}
                    </div>
                    {valuation && (
                      <div className="text-right ml-4">
                        <p className="text-lg font-semibold text-hn-dark">
                          ${valuation.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500">Valuation</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="p-5 bg-white rounded-xl shadow text-center">
              <p className="text-gray-600">No properties yet</p>
              <button
                onClick={() => navigate("/add-property")}
                className="mt-4 px-4 py-2 bg-hn-red text-white rounded-lg"
              >
                Add Your First Property
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ---------------------------------------------------------
         PORTFOLIO MAP
      --------------------------------------------------------- */}
      {properties.length > 0 && <PortfolioMap properties={properties} />}

    </div>
  );
}
